// src/components/ceremony/PovResultOverlay.js
// ============================================================================
// Founder sequence step 5:
//   "Vision detects win/loss/quit per POV -> that POV fades to the dynamic
//    results screen with winner payouts; if the user QUIT -> 'awaiting results'
//    b-roll with music + option to switch camera to other streamers."
//
// WHY THIS SURFACE HAS TO EXIST (not just polish):
// A confirmed terminal outcome ENDS that racer's broadcast server-side
// (liveCapture calls endMatchStreamForUser), which finalizes the HLS manifest.
// The spectator's player doesn't get an event — it throws a fatal error and the
// picture dies. Until now that was the entire post-race experience for whoever
// happened to be watching that camera. This covers the feed at the moment it
// goes, and says what happened.
//
// It mounts over ONE camera, not the whole screen: the other feeds are still
// racing, so the ceremony must never take the picture away from them.
// ============================================================================

import React from 'react';
import { View, Text, Image, Pressable, StyleSheet } from 'react-native';

import { CEREMONY_COLORS as C, POV_STATE } from '../../constants/ceremony';
import { anim, IS_WEB, DIE_PURPLE_20, LOGO } from './CeremonyCanvas';

export default function PovResultOverlay({ pov, onSwitchCamera, canSwitch }) {
  if (!pov || pov.state === POV_STATE.RACING) return null;

  const name = pov.ign || pov.name;
  const quit = pov.state === POV_STATE.DNF;
  const review = pov.state === POV_STATE.REVIEW;

  return (
    <View style={[styles.root, anim('fadeIn', { duration: 900 })]}>
      <View style={styles.scrim} pointerEvents="none" />

      <View style={[styles.card, anim('fadeUp', { delay: 200, duration: 600 })]}>
        <Image source={LOGO} style={styles.logo} resizeMode="contain" />

        {quit ? (
          <>
            <Text style={styles.kickerDnf}>OUT</Text>
            <Text style={styles.name} numberOfLines={1}>{name}</Text>
            <Text style={styles.body}>Awaiting the rest of the players.</Text>
          </>
        ) : review ? (
          <>
            <Text style={styles.kickerReview}>UNDER REVIEW</Text>
            <Text style={styles.name} numberOfLines={1}>{name}</Text>
            <Text style={styles.body}>This finish is being checked.</Text>
          </>
        ) : (
          <>
            <Text style={styles.kicker}>{pov.finalRank ? 'FINISHED' : 'MATCH COMPLETE'}</Text>
            <Text style={styles.name} numberOfLines={1}>{name}</Text>
            {pov.finalRank ? (
              <Text style={[styles.position, pov.finalRank === 1 && styles.positionWin]}>
                {pov.finalRank === 1 ? 'P1' : `P${pov.finalRank}`}
              </Text>
            ) : null}
            {pov.crits > 0 ? (
              <View style={styles.critsRow}>
                <Image source={DIE_PURPLE_20} style={styles.critsDie} resizeMode="contain" />
                <Text style={styles.critsAmt}>{pov.crits.toLocaleString('en-US')}</Text>
                <Text style={styles.critsLbl}>CRITS</Text>
              </View>
            ) : (
              <Text style={styles.bodyDim}>Final standings when the event ends.</Text>
            )}
          </>
        )}

        {canSwitch && (
          <Pressable
            style={({ pressed }) => [styles.switchBtn, pressed && styles.switchBtnPressed]}
            onPress={onSwitchCamera}
            accessibilityRole="button"
          >
            <Text style={styles.switchText}>SWITCH CAMERA</Text>
          </Pressable>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 15,
  },
  scrim: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.82)',
  },
  card: {
    alignItems: 'center',
    paddingHorizontal: 44,
    paddingVertical: 34,
    borderRadius: 16,
    backgroundColor: 'rgba(12,12,12,0.94)',
    borderWidth: 1,
    borderColor: '#2a2a2a',
    maxWidth: '86%',
  },
  logo: { width: 132, height: 46, marginBottom: 14, opacity: 0.9 },
  kicker: { fontSize: 15, fontWeight: '900', letterSpacing: 6, color: C.CYAN_SOFT },
  kickerDnf: { fontSize: 15, fontWeight: '900', letterSpacing: 6, color: C.DNF },
  kickerReview: { fontSize: 15, fontWeight: '900', letterSpacing: 6, color: C.GOLD },
  name: {
    marginTop: 10,
    fontSize: 34,
    fontWeight: '900',
    letterSpacing: 1,
    color: '#ffffff',
    textAlign: 'center',
  },
  position: {
    marginTop: 8,
    fontSize: 56,
    fontWeight: '900',
    letterSpacing: 4,
    color: C.GOLD,
    ...(IS_WEB ? { textShadow: '0 0 26px rgba(252,208,0,0.45)' } : {}),
  },
  positionWin: {
    fontSize: 72,
    ...(IS_WEB ? { textShadow: '0 0 40px rgba(252,208,0,0.7)' } : {}),
  },
  body: { marginTop: 10, fontSize: 16, fontWeight: '700', color: C.DIM, textAlign: 'center' },
  bodyDim: { marginTop: 10, fontSize: 15, fontWeight: '700', color: '#666666', textAlign: 'center' },
  critsRow: { marginTop: 14, flexDirection: 'row', alignItems: 'flex-end', gap: 8 },
  critsDie: { width: 30, height: 30 },
  critsAmt: {
    fontSize: 34,
    fontWeight: '900',
    color: C.PURPLE,
    ...(IS_WEB ? { textShadow: '0 0 18px rgba(160,80,255,0.5)' } : {}),
  },
  critsLbl: { fontSize: 13, fontWeight: '800', color: C.DIM, letterSpacing: 2, paddingBottom: 5 },
  switchBtn: {
    marginTop: 22,
    paddingHorizontal: 26,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: C.GOLD,
    backgroundColor: 'rgba(252,208,0,0.08)',
  },
  switchBtnPressed: { backgroundColor: 'rgba(252,208,0,0.2)' },
  switchText: { fontSize: 15, fontWeight: '900', letterSpacing: 3, color: C.GOLD },
});
